import { JQL_ARRAY_FIELD_NAMES, JQL_DATE_FIELD_NAMES, JQL_FIELD_NAMES, JQL_TEXT_FIELD_NAMES } from "./jql";

// ── Public types ──────────────────────────────────────────────────────────

export interface JqlStatusOption {
  id: string;
  name: string;
}

// A selectable workspace member for assignee/reporter value suggestions — id is the stored
// JQL value (assignee/reporter compare by user id), name is the display label.
export interface JqlMemberOption {
  id: string;
  name: string;
}

// Enumerable values the suggester offers for each field. Built from the live board
// config so suggestions reflect the actual projects/labels/people, not a static list.
export interface JqlSuggestContext {
  priorities: readonly string[];
  types: readonly string[];
  statuses: readonly JqlStatusOption[];
  assignees: readonly JqlMemberOption[];
  labels: readonly string[];
  projects: readonly string[];
  currentUser: string;
}

export type JqlSuggestionKind = "field" | "operator" | "value" | "keyword";

export interface JqlSuggestion {
  // Text spliced into the input when the suggestion is accepted (already quoted and
  // carrying its own trailing separator so the next token starts cleanly).
  insert: string;
  // Primary text shown in the dropdown row.
  label: string;
  // Optional muted hint on the right (e.g. a status' stored id behind its display name).
  hint?: string;
  kind: JqlSuggestionKind;
}

export interface JqlSuggestResult {
  suggestions: JqlSuggestion[];
  // Half-open range [replaceStart, replaceEnd) in the input that an accepted
  // suggestion overwrites.
  replaceStart: number;
  replaceEnd: number;
}

const MAX_SUGGESTIONS: number = 30;

const DATE_FIELDS: ReadonlySet<string> = new Set<string>(JQL_DATE_FIELD_NAMES);
const ARRAY_FIELDS: ReadonlySet<string> = new Set<string>(JQL_ARRAY_FIELD_NAMES);
const TEXT_FIELDS: ReadonlySet<string> = new Set<string>(JQL_TEXT_FIELD_NAMES);
const FIELDS: ReadonlySet<string> = new Set<string>(JQL_FIELD_NAMES);

const COMPARE_OPS: ReadonlySet<string> = new Set<string>(["=", "!=", "~", ">", "<", ">=", "<="]);
const WORD_BREAK: string = ' \t()=!~,"<>';

// ── Tokenizer with source spans ───────────────────────────────────────────

type TokenKind = "word" | "op" | "paren" | "comma";

interface TokenSpan {
  text: string;
  start: number;
  end: number;
  kind: TokenKind;
}

function tokenizeSpans(input: string): TokenSpan[] {
  const tokens: TokenSpan[] = [];
  let i: number = 0;
  while (i < input.length) {
    const ch: string = input[i]!;
    if (ch === " " || ch === "\t") {
      i += 1;
      continue;
    }
    if (ch === '"') {
      let end: number = input.indexOf('"', i + 1);
      const closed: boolean = end !== -1;
      if (!closed) end = input.length;
      tokens.push({ text: input.slice(i + 1, end), start: i, end: closed ? end + 1 : end, kind: "word" });
      i = closed ? end + 1 : end;
      continue;
    }
    if (ch === "<" || ch === ">") {
      if (input[i + 1] === "=") {
        tokens.push({ text: ch + "=", start: i, end: i + 2, kind: "op" });
        i += 2;
        continue;
      }
      tokens.push({ text: ch, start: i, end: i + 1, kind: "op" });
      i += 1;
      continue;
    }
    if (ch === "!" && input[i + 1] === "=") {
      tokens.push({ text: "!=", start: i, end: i + 2, kind: "op" });
      i += 2;
      continue;
    }
    if (ch === "=" || ch === "~" || ch === "!") {
      tokens.push({ text: ch, start: i, end: i + 1, kind: "op" });
      i += 1;
      continue;
    }
    if (ch === "(" || ch === ")") {
      tokens.push({ text: ch, start: i, end: i + 1, kind: "paren" });
      i += 1;
      continue;
    }
    if (ch === ",") {
      tokens.push({ text: ch, start: i, end: i + 1, kind: "comma" });
      i += 1;
      continue;
    }
    let j: number = i;
    while (j < input.length && !WORD_BREAK.includes(input[j]!)) j += 1;
    tokens.push({ text: input.slice(i, j), start: i, end: j, kind: "word" });
    i = j;
  }
  return tokens;
}

// ── Caret context ─────────────────────────────────────────────────────────

type ParseState = "FIELD" | "OP" | "VALUE" | "LIST" | "EMPTY" | "JOINER";

interface CaretContext {
  fragment: string;
  replaceStart: number;
  replaceEnd: number;
  prior: TokenSpan[];
}

function locate(input: string, tokens: TokenSpan[], caret: number): CaretContext {
  const cur: TokenSpan | undefined = tokens.find((t: TokenSpan) => caret > t.start && caret <= t.end);
  if (cur && cur.kind === "word") {
    const fragmentRaw: string = input.slice(cur.start, caret);
    const fragment: string = fragmentRaw.startsWith('"') ? fragmentRaw.slice(1) : fragmentRaw;
    return {
      fragment,
      replaceStart: cur.start,
      replaceEnd: cur.end,
      prior: tokens.filter((t: TokenSpan) => t.end <= cur.start)
    };
  }
  // Caret sits in whitespace or just after punctuation — insert a fresh token here.
  return {
    fragment: "",
    replaceStart: caret,
    replaceEnd: caret,
    prior: tokens.filter((t: TokenSpan) => t.end <= caret)
  };
}

interface MachineState {
  state: ParseState;
  field: string;
}

// Walks the tokens before the caret through the JQL grammar to decide what may come
// next: a field, an operator, a value, a list value, an emptiness keyword, or a joiner.
function runMachine(prior: TokenSpan[]): MachineState {
  let state: ParseState = "FIELD";
  let field: string = "";
  for (const token of prior) {
    const lower: string = token.text.toLowerCase();
    if (state === "FIELD") {
      if (FIELDS.has(lower)) {
        field = lower;
        state = "OP";
      }
    } else if (state === "OP") {
      if (lower === "is") state = "EMPTY";
      else if (lower === "in") state = "LIST";
      else if (lower === "not") state = "OP";
      else if (COMPARE_OPS.has(token.text)) state = "VALUE";
    } else if (state === "VALUE") {
      state = "JOINER";
    } else if (state === "LIST") {
      if (token.text === ")") state = "JOINER";
    } else if (state === "EMPTY") {
      if (lower === "empty") state = "JOINER";
    } else if (state === "JOINER") {
      if (lower === "and" || lower === "or") state = "FIELD";
    }
  }
  return { state, field };
}

// ── Suggestion builders ───────────────────────────────────────────────────

function quote(value: string): string {
  return /\s/.test(value) ? `"${value}"` : value;
}

function fieldSuggestions(): JqlSuggestion[] {
  return JQL_FIELD_NAMES.map((name: string) => ({ insert: `${name} `, label: name, kind: "field" }));
}

function operatorSuggestions(field: string): JqlSuggestion[] {
  let ops: string[];
  if (DATE_FIELDS.has(field)) {
    ops = ["=", "!=", ">", ">=", "<", "<=", "is empty", "is not empty"];
  } else if (ARRAY_FIELDS.has(field)) {
    ops = ["=", "!=", "in", "not in", "~", "is empty", "is not empty"];
  } else if (TEXT_FIELDS.has(field)) {
    ops = ["~", "=", "!="];
  } else {
    ops = ["=", "!=", "in", "not in", "~", "is empty", "is not empty"];
  }
  return ops.map((op: string) => {
    if (op === "in") return { insert: "in (", label: "in (…)", kind: "operator" };
    if (op === "not in") return { insert: "not in (", label: "not in (…)", kind: "operator" };
    return { insert: `${op} `, label: op, kind: "operator" };
  });
}

function valueSuggestions(field: string, ctx: JqlSuggestContext, inList: boolean): JqlSuggestion[] {
  if (DATE_FIELDS.has(field)) {
    const presets: JqlSuggestion[] = [
      { insert: "now() ", label: "now()", hint: "current time", kind: "value" },
      { insert: "-1d ", label: "-1d", hint: "1 day ago", kind: "value" },
      { insert: "-7d ", label: "-7d", hint: "7 days ago", kind: "value" },
      { insert: "-30d ", label: "-30d", hint: "30 days ago", kind: "value" },
      { insert: "7d ", label: "7d", hint: "in 7 days", kind: "value" }
    ];
    return presets;
  }
  if (field === "priority") {
    return ctx.priorities.map((value: string) => ({ insert: `${quote(value)} `, label: value, kind: "value" }));
  }
  if (field === "type") {
    return ctx.types.map((value: string) => ({ insert: `${quote(value)} `, label: value, kind: "value" }));
  }
  if (field === "status") {
    return ctx.statuses.map((status: JqlStatusOption) => ({
      insert: `${quote(status.id)} `,
      label: status.name,
      hint: status.id === status.name ? undefined : status.id,
      kind: "value"
    }));
  }
  if (field === "project") {
    return ctx.projects.map((value: string) => ({ insert: `${quote(value)} `, label: value, kind: "value" }));
  }
  if (field === "labels") {
    return ctx.labels.map((value: string) => ({ insert: `${quote(value)} `, label: value, kind: "value" }));
  }
  if (field === "assignee" || field === "reporter") {
    const people: JqlSuggestion[] = ctx.assignees.map((member: JqlMemberOption) => ({
      insert: `${quote(member.id)} `,
      label: member.name,
      hint: member.id === member.name ? undefined : member.id,
      kind: "value"
    }));
    // currentUser() only makes sense as a single value, not inside an in(...) list.
    if (!inList) {
      people.unshift({ insert: "currentUser() ", label: "currentUser()", hint: ctx.currentUser, kind: "value" });
    }
    return people;
  }
  return [];
}

function keywordSuggestions(): JqlSuggestion[] {
  return [
    { insert: "AND ", label: "AND", kind: "keyword" },
    { insert: "OR ", label: "OR", kind: "keyword" }
  ];
}

function emptySuggestions(): JqlSuggestion[] {
  return [
    { insert: "empty ", label: "empty", kind: "keyword" },
    { insert: "not empty ", label: "not empty", kind: "keyword" }
  ];
}

function matches(suggestion: JqlSuggestion, fragment: string): boolean {
  if (!fragment) return true;
  const needle: string = fragment.toLowerCase();
  if (suggestion.kind === "value") {
    return suggestion.label.toLowerCase().includes(needle) || suggestion.insert.toLowerCase().includes(needle);
  }
  return suggestion.label.toLowerCase().startsWith(needle) || suggestion.insert.trim().toLowerCase().startsWith(needle);
}

// Computes autocomplete suggestions for `input` given the caret position. Pure and
// synchronous — the caller memoizes on (input, caret, context).
export function suggestJql(input: string, caret: number, ctx: JqlSuggestContext): JqlSuggestResult {
  const clamped: number = Math.max(0, Math.min(caret, input.length));
  const tokens: TokenSpan[] = tokenizeSpans(input);
  const located: CaretContext = locate(input, tokens, clamped);
  const machine: MachineState = runMachine(located.prior);

  let pool: JqlSuggestion[];
  if (machine.state === "FIELD") pool = fieldSuggestions();
  else if (machine.state === "OP") pool = operatorSuggestions(machine.field);
  else if (machine.state === "VALUE") pool = valueSuggestions(machine.field, ctx, false);
  else if (machine.state === "LIST") pool = valueSuggestions(machine.field, ctx, true);
  else if (machine.state === "EMPTY") pool = emptySuggestions();
  else pool = keywordSuggestions();

  const suggestions: JqlSuggestion[] = pool
    .filter((s: JqlSuggestion) => matches(s, located.fragment))
    .slice(0, MAX_SUGGESTIONS);

  return { suggestions, replaceStart: located.replaceStart, replaceEnd: located.replaceEnd };
}
